/**
 * Format a date string according to the organization's configured date format.
 *
 * Supported format tokens:
 *   DD  – day of month (zero-padded)
 *   MM  – month number (zero-padded)
 *   YYYY – four-digit year
 *   YY  – two-digit year
 *   MMM  – short month name (Jan, Feb, …)
 *   MMMM – full month name (January, February, …)
 *
 * Examples of format strings:
 *   "DD/MM/YYYY"  → 21/05/2026
 *   "MM/DD/YYYY"  → 05/21/2026
 *   "YYYY-MM-DD"  → 2026-05-21
 *   "DD MMM YYYY" → 21 May 2026
 *   "MMM DD, YYYY"→ May 21, 2026
 */

const SHORT_MONTHS = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
];

const FULL_MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

/**
 * Format a date value using a pattern string.
 * @param {string|Date|number|null|undefined} value – the date to format
 * @param {string} pattern – format pattern, e.g. "DD/MM/YYYY"
 * @returns {string} formatted date string
 */
export function formatDate(value, pattern = 'DD/MM/YYYY') {
  if (!value) return '—';

  let date;
  try {
    date = value instanceof Date ? value : new Date(value);
    // Guard against Invalid Date
    if (Number.isNaN(date.getTime())) return String(value);
  } catch {
    return String(value);
  }

  const day = date.getDate();          // 1-31
  const month = date.getMonth();       // 0-11
  const year = date.getFullYear();

  const dd = String(day).padStart(2, '0');
  const mm = String(month + 1).padStart(2, '0');
  const yyyy = String(year);
  const yy = yyyy.slice(-2);
  const mmm = SHORT_MONTHS[month];
  const mmmm = FULL_MONTHS[month];

  // Simple replacements – order matters (longer tokens first)
  return pattern
    .replace('MMMM', mmmm)
    .replace('MMM', mmm)
    .replace('YYYY', yyyy)
    .replace('YY', yy)
    .replace('DD', dd)
    .replace('MM', mm);
}

export default formatDate;